import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, lastDayOfMonth, isLastDayOfMonth } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DollarSign, FileSpreadsheet, FileText, Receipt, CheckCircle, AlertCircle, Mail, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  CommissionsFilters,
  CommissionsSummaryTable,
  CommissionsDetailModal,
  ConfirmPaymentDialog,
  API_URL,
  formatCurrency,
  formatDate
} from '../components/commissions';

const Commissions = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState([]);
  const [totals, setTotals] = useState({ total_pacientes: 0, total_facturado: 0, total_comision: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [datePreset, setDatePreset] = useState('this_month');
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  
  // Detail modal
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Confirm payment dialog
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [doctorToPay, setDoctorToPay] = useState(null);
  
  // Generate expense dialog
  const [isGenerateExpenseDialogOpen, setIsGenerateExpenseDialogOpen] = useState(false);
  const [expenseStatus, setExpenseStatus] = useState(null);
  const [generatingExpense, setGeneratingExpense] = useState(false);
  
  // Email dialog
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    updateDateRange(datePreset);
  }, [datePreset]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchSummary();
      checkExpenseStatus();
    }
  }, [startDate, endDate]);

  const checkExpenseStatus = async () => {
    try {
      const [year, month] = startDate.split('-');
      const response = await axios.get(`${API_URL}/api/commissions/expense-status`, {
        params: { year: parseInt(year), month: parseInt(month) },
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenseStatus(response.data);
    } catch (error) {
      console.error('Error checking expense status:', error);
    }
  };

  const handleGenerateExpense = async () => {
    setGeneratingExpense(true);
    try {
      const [year, month] = startDate.split('-');
      const response = await axios.post(`${API_URL}/api/commissions/generate-expense`, null, {
        params: { year: parseInt(year), month: parseInt(month) },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Gasto de comisiones generado: ${formatCurrency(response.data.total_commission)}`);
      setIsGenerateExpenseDialogOpen(false);
      checkExpenseStatus();
    } catch (error) {
      console.error('Error generating expense:', error);
      toast.error(error.response?.data?.detail || 'Error al generar el gasto');
    } finally {
      setGeneratingExpense(false);
    }
  };

  // Generate PDF and send via email
  const handleSendEmail = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      toast.error('Por favor ingresa un email válido');
      return;
    }
    
    setSendingEmail(true);
    try {
      // Generate PDF
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text('Reporte de Comisiones', 14, 22);
      doc.setFontSize(12);
      doc.text(`Período: ${startDate} al ${endDate}`, 14, 32);
      
      const tableData = filteredSummary.map(row => [
        row.medico_referente,
        row.cantidad_pacientes,
        formatCurrency(row.total_facturado),
        formatCurrency(row.comision)
      ]);
      
      tableData.push(['TOTAL', totals.total_pacientes, formatCurrency(totals.total_facturado), formatCurrency(totals.total_comision)]);
      
      autoTable(doc, {
        startY: 40,
        head: [['Médico Referente', 'Pacientes', 'Total Facturado', 'Comisión (13%)']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [26, 81, 146] }
      });
      
      // Convert to base64
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      
      // Build summary text
      const summary = `Total Médicos: ${filteredSummary.length}\nTotal Pacientes: ${totals.total_pacientes}\nTotal Facturado: ${formatCurrency(totals.total_facturado)}\nComisión Total (13%): ${formatCurrency(totals.total_comision)}`;
      
      // Send email
      await axios.post(`${API_URL}/api/email/send-report`, {
        to_email: emailTo,
        report_type: 'commissions',
        period: `${startDate} al ${endDate}`,
        summary: summary,
        pdf_base64: pdfBase64,
        filename: `Comisiones_${startDate}_${endDate}.pdf`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Reporte enviado a ${emailTo}`);
      setIsEmailDialogOpen(false);
      setEmailTo('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el email');
    } finally {
      setSendingEmail(false);
    }
  };

  const updateDateRange = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'this_month':
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case 'last_month':
        start = startOfMonth(subMonths(today, 1));
        end = endOfMonth(subMonths(today, 1));
        break;
      case 'last_3_months':
        start = startOfMonth(subMonths(today, 2));
        end = endOfMonth(today);
        break;
      case 'this_year':
        start = startOfYear(today);
        end = endOfMonth(today);
        break;
      case 'custom':
        return; // Don't change dates for custom
      default:
        start = startOfMonth(today);
        end = endOfMonth(today);
    }

    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/commissions/summary`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data.summary);
      setTotals(response.data.totals);
    } catch (error) {
      console.error('Error fetching commissions:', error);
      toast.error('Error al cargar las comisiones');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (medicoReferente) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/commissions/detail/${encodeURIComponent(medicoReferente)}`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailData(response.data);
    } catch (error) {
      console.error('Error fetching detail:', error);
      toast.error('Error al cargar el detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetailModal = (doctor) => {
    setSelectedDoctor(doctor);
    setIsDetailModalOpen(true);
    fetchDetail(doctor.medico_referente);
  };

  const handleMarkAsPaid = async () => {
    if (!doctorToPay) return;
    
    try {
      await axios.post(`${API_URL}/api/commissions/mark-paid`, null, {
        params: {
          medico_referente: doctorToPay.medico_referente,
          period_start: startDate,
          period_end: endDate,
          total_facturado: doctorToPay.total_facturado,
          comision: doctorToPay.comision
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Comisión de Q${doctorToPay.comision.toFixed(2)} marcada como pagada`);
      setIsConfirmDialogOpen(false);
      setDoctorToPay(null);
      fetchSummary();
      
      if (isDetailModalOpen && detailData) {
        fetchDetail(doctorToPay.medico_referente);
      }
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error(error.response?.data?.detail || 'Error al marcar como pagada');
    }
  };

  const filteredSummary = useMemo(() => {
    if (!searchTerm) return summary;
    return summary.filter(s => 
      s.medico_referente.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [summary, searchTerm]);

  // Export to Excel
  const exportToExcel = (type = 'summary') => {
    const workbook = XLSX.utils.book_new();
    const periodTitle = `Período: ${formatDate(startDate)} al ${formatDate(endDate)}`;

    if (type === 'summary') {
      const summaryData = [
        ['Oftalmodiagnosis — Reporte de Comisiones Médicos Referentes'],
        [periodTitle],
        [],
        ['Médico Referente', 'Cant. Referencias', 'Total Facturado', 'Comisión 13%', 'Estado', 'Banco', 'No. Cuenta', 'Tipo Cuenta'],
        ...filteredSummary.map(s => [
          s.medico_referente,
          s.cantidad_pacientes,
          s.total_facturado,
          s.comision,
          s.pagado ? 'Pagado' : 'Pendiente',
          s.banco || '-',
          s.numero_cuenta || '-',
          s.tipo_cuenta || '-'
        ]),
        [],
        ['TOTALES', totals.total_pacientes, totals.total_facturado, totals.total_comision, '', '', '', '']
      ];
      
      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 18 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, ws1, 'Resumen');

      const allDetails = [];
      allDetails.push(['Oftalmodiagnosis — Detalle de Comisiones']);
      allDetails.push([periodTitle]);
      allDetails.push([]);
      allDetails.push(['Médico Referente', 'Paciente', 'Fecha', 'Tipo Examen', 'Costo', 'Descuento', 'Total', 'Comisión 13%']);
      
      const ws2 = XLSX.utils.aoa_to_sheet(allDetails);
      ws2['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, ws2, 'Detalle');

      const filename = `Comisiones_Resumen_${startDate}_${endDate}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success('Reporte exportado exitosamente');
    } else if (type === 'detail' && detailData) {
      const doctorInfo = summary.find(s => s.medico_referente === detailData.medico_referente);
      
      const detailRows = [
        ['Oftalmodiagnosis — Reporte de Comisiones'],
        [`Médico: ${detailData.medico_referente}`],
        [periodTitle],
        [],
        ['DATOS BANCARIOS'],
        ['Banco:', doctorInfo?.banco || 'No registrado'],
        ['Número de Cuenta:', doctorInfo?.numero_cuenta || 'No registrado'],
        ['Tipo de Cuenta:', doctorInfo?.tipo_cuenta || 'No registrado'],
        [],
        ['DETALLE DE REFERENCIAS'],
        ['Paciente', 'Fecha', 'Tipo Examen', 'Ojo', 'Costo', 'Descuento', 'Total', 'Comisión 13%'],
        ...detailData.patients.map(p => [
          p.nombre,
          p.fecha,
          p.tipo_examen,
          p.ojo_estudiar || '-',
          p.precio,
          p.tipo_descuento === 'porcentaje' ? `${p.descuento}%` : p.descuento,
          p.total,
          p.comision
        ]),
        [],
        ['TOTALES', '', '', '', detailData.totals.total_precio, '', detailData.totals.total_facturado, detailData.totals.total_comision],
        [],
        ['RESUMEN DE COMISIÓN'],
        ['Total Estudios Referidos:', detailData.patients.length],
        ['Total Monetario:', detailData.totals.total_facturado],
        ['Comisión a Pagar (13%):', detailData.totals.total_comision]
      ];

      const ws = XLSX.utils.aoa_to_sheet(detailRows);
      ws['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, ws, detailData.medico_referente.substring(0, 31));

      const safeName = detailData.medico_referente.replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `Comisiones_${safeName}_${startDate}_${endDate}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success('Detalle exportado exitosamente');
    }
  };

  // Export to PDF
  const exportToPDF = (type = 'summary') => {
    const doc = new jsPDF();
    const periodTitle = `Período: ${formatDate(startDate)} al ${formatDate(endDate)}`;
    
    doc.setFontSize(18);
    doc.setTextColor(26, 81, 146);
    doc.text('Oftalmodiagnosis', 14, 20);
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Reporte de Comisiones Médicos Referentes', 14, 28);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(periodTitle, 14, 35);

    if (type === 'summary') {
      const tableData = filteredSummary.map(s => [
        s.medico_referente,
        s.cantidad_pacientes.toString(),
        formatCurrency(s.total_facturado),
        formatCurrency(s.comision),
        s.pagado ? 'Pagado' : 'Pendiente',
        s.banco || '-',
        s.numero_cuenta || '-',
        s.tipo_cuenta || '-'
      ]);

      autoTable(doc, {
        startY: 42,
        head: [['Médico', 'Ref.', 'Total Fact.', 'Comisión', 'Estado', 'Banco', 'No. Cuenta', 'Tipo']],
        body: tableData,
        foot: [[
          'TOTALES',
          totals.total_pacientes.toString(),
          formatCurrency(totals.total_facturado),
          formatCurrency(totals.total_comision),
          '', '', '', ''
        ]],
        theme: 'striped',
        headStyles: { fillColor: [26, 81, 146], fontSize: 8 },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 7, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 12, halign: 'center' },
          2: { cellWidth: 22, halign: 'right' },
          3: { cellWidth: 22, halign: 'right' },
          4: { cellWidth: 18 },
          5: { cellWidth: 28 },
          6: { cellWidth: 25 },
          7: { cellWidth: 18 }
        }
      });

      const filename = `Comisiones_Resumen_${startDate}_${endDate}.pdf`;
      doc.save(filename);
      toast.success('PDF exportado exitosamente');
    } else if (type === 'detail' && detailData) {
      const doctorInfo = summary.find(s => s.medico_referente === detailData.medico_referente);
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Médico: ${detailData.medico_referente}`, 14, 42);
      
      doc.setFillColor(240, 247, 255);
      doc.rect(14, 46, 182, 22, 'F');
      doc.setFontSize(10);
      doc.setTextColor(26, 81, 146);
      doc.text('DATOS BANCARIOS', 16, 53);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.text(`Banco: ${doctorInfo?.banco || 'No registrado'}`, 16, 59);
      doc.text(`No. Cuenta: ${doctorInfo?.numero_cuenta || 'No registrado'}`, 80, 59);
      doc.text(`Tipo: ${doctorInfo?.tipo_cuenta || 'No registrado'}`, 150, 59);

      const tableData = detailData.patients.map(p => [
        p.nombre,
        p.fecha,
        p.tipo_examen,
        formatCurrency(p.precio),
        p.tipo_descuento === 'porcentaje' ? `${p.descuento}%` : formatCurrency(p.descuento),
        formatCurrency(p.total),
        formatCurrency(p.comision)
      ]);

      autoTable(doc, {
        startY: 72,
        head: [['Paciente', 'Fecha', 'Examen', 'Precio', 'Desc.', 'Total', 'Comisión']],
        body: tableData,
        foot: [[
          'TOTALES',
          '',
          '',
          formatCurrency(detailData.totals.total_precio),
          '',
          formatCurrency(detailData.totals.total_facturado),
          formatCurrency(detailData.totals.total_comision)
        ]],
        theme: 'striped',
        headStyles: { fillColor: [26, 81, 146], fontSize: 8 },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 22 },
          2: { cellWidth: 30 },
          3: { cellWidth: 22, halign: 'right' },
          4: { cellWidth: 18, halign: 'right' },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 22, halign: 'right' }
        }
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFillColor(16, 185, 129);
      doc.rect(14, finalY, 80, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.text('RESUMEN DE COMISIÓN', 18, finalY + 8);
      doc.setFontSize(9);
      doc.text(`Total Estudios: ${detailData.patients.length}`, 18, finalY + 15);
      doc.text(`Total Facturado: ${formatCurrency(detailData.totals.total_facturado)}`, 18, finalY + 21);
      
      doc.setFillColor(26, 81, 146);
      doc.rect(96, finalY, 100, 25, 'F');
      doc.setFontSize(12);
      doc.text('COMISIÓN A PAGAR (13%)', 100, finalY + 10);
      doc.setFontSize(16);
      doc.text(formatCurrency(detailData.totals.total_comision), 100, finalY + 20);

      const safeName = detailData.medico_referente.replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `Comisiones_${safeName}_${startDate}_${endDate}.pdf`;
      doc.save(filename);
      toast.success('PDF exportado exitosamente');
    }
  };

  return (
    <div className="space-y-6" data-testid="commissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Comisiones Médicos</h2>
            <p className="text-sm text-slate-500">Gestiona las comisiones de médicos referentes (13%)</p>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Generate Expense Button */}
          {expenseStatus && !expenseStatus.generated && totals.total_comision > 0 && (
            <Button
              onClick={() => setIsGenerateExpenseDialogOpen(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="generate-expense-button"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Generar Gasto
            </Button>
          )}
          {expenseStatus && expenseStatus.generated && (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">
                Gasto pagado: {formatCurrency(expenseStatus.total_amount)}
              </span>
            </div>
          )}
          <Button
            onClick={() => exportToExcel('summary')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-excel-button"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button
            onClick={() => exportToPDF('summary')}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-50"
            data-testid="export-pdf-button"
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button
            onClick={() => setIsEmailDialogOpen(true)}
            variant="outline"
            className="border-blue-500 text-blue-500 hover:bg-blue-50"
            data-testid="send-email-button"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
        </div>
      </div>

      {/* Filters */}
      <CommissionsFilters
        datePreset={datePreset}
        setDatePreset={setDatePreset}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      {/* Summary Table */}
      <CommissionsSummaryTable
        loading={loading}
        filteredSummary={filteredSummary}
        totals={totals}
        onViewDetail={openDetailModal}
        onMarkAsPaid={(doctor) => {
          setDoctorToPay(doctor);
          setIsConfirmDialogOpen(true);
        }}
      />

      {/* Detail Modal */}
      <CommissionsDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        loading={detailLoading}
        detailData={detailData}
        onExportExcel={() => exportToExcel('detail')}
        onExportPDF={() => exportToPDF('detail')}
        onMarkAsPaid={(doctor) => {
          setDoctorToPay(doctor);
          setIsConfirmDialogOpen(true);
        }}
      />

      {/* Confirm Payment Dialog */}
      <ConfirmPaymentDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        doctorToPay={doctorToPay}
        onConfirm={handleMarkAsPaid}
      />

      {/* Generate Expense Dialog */}
      <AlertDialog open={isGenerateExpenseDialogOpen} onOpenChange={setIsGenerateExpenseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-600" />
              Generar Gasto de Comisiones
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Se creará un gasto fijo con el total de comisiones del período seleccionado.
                  Este gasto se registrará en el módulo de Gastos para su seguimiento y pago.
                </p>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Período:</span>
                      <p className="font-medium">{startDate} al {endDate}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Total Facturado:</span>
                      <p className="font-medium">{formatCurrency(totals.total_facturado)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Médicos con referencias:</span>
                      <p className="font-medium">{summary.length}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Total Pacientes:</span>
                      <p className="font-medium">{totals.total_pacientes}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">Comisión a registrar (13%):</span>
                      <span className="text-xl font-bold text-purple-700">{formatCurrency(totals.total_comision)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">
                    El gasto se registrará y marcará como <strong>pagado automáticamente</strong>. Solo puede generarse una vez por período.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={generatingExpense}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleGenerateExpense}
              disabled={generatingExpense}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generatingExpense ? 'Generando...' : 'Generar Gasto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Dialog */}
      <AlertDialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Enviar Reporte por Email
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Se enviará el reporte de comisiones del período <strong>{startDate}</strong> al <strong>{endDate}</strong> en formato PDF.
                </p>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Correo destinatario:</label>
                  <Input
                    type="email"
                    placeholder="ejemplo@correo.com"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <strong>Resumen del reporte:</strong><br />
                    • {filteredSummary.length} médico(s) referente(s)<br />
                    • {totals.total_pacientes} paciente(s) referido(s)<br />
                    • Total facturado: {formatCurrency(totals.total_facturado)}<br />
                    • Comisión total: {formatCurrency(totals.total_comision)}
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingEmail}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailTo}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Commissions;
